/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  OutlinedInput,
  Tooltip,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import Button from 'components/Button';
import TableItems, {
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import {
  getAllSessionsInInstanceRequest,
  removeSessionsInstanceRequest,
} from 'modules/environments/actions';
import {
  ListSessionsInInstanceFieldsProps,
  SessionsInInstance,
} from 'modules/environments/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { cyrb53Hash, formatDateWithMoment } from 'utils/helpers';
import { useTableFilters } from 'hooks/useTableFilters';
import { PermissionsData } from 'modules/authenticate/interfaces';
import FormRemoveSession from './FormRemoveSession';
import styles from './styles';

const useStyles = makeStyles(styles);

const mapStatus = (status: string) => {
  const map = {
    init: <CircularProgress size={16} color="secondary" />,
    removing: <CircularProgress size={16} color="secondary" />,
    active: <StatusIcon status="success" />,
    close: <StatusIcon status="blocked" />,
  };

  return map[status] || status;
};

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};
interface Props {
  environmentId: string;
  instanceId: string;
  permissions?: PermissionsData;
  sessionsInInstanceList: ListSessionsInInstanceFieldsProps;
  getAllSessionsInInstanceRequest: ({
    environmentId,
    instanceId,
    query,
  }) => void;
  removeSessionsInstanceRequest: ({ values }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const InstanceSessions = ({
  environmentId,
  instanceId,
  permissions,
  sessionsInInstanceList,
  getAllSessionsInInstanceRequest,
  removeSessionsInstanceRequest,
  modalOpen,
  modalClose,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [checks, setChecks] = React.useState<string[]>([]);
  const [selectedAll, setSelectedAll] = React.useState(false);

  const handleRemoveSessions = (sessions: any[], type: string) => {
    let title = i18n.t('CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_TITLE_KEY');
    let subtitle = i18n.t(
      'CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_DESCRIPTION_KEY'
    );

    if (type !== 'multiple') {
      title = i18n.t('REMOVE_SESSION_KEY');
      subtitle = i18n.t('REMOVE_SESSION_DESCRIPTION_KEY', {
        name: `${sessions[0]?.user?.first_name} ${sessions[0]?.user?.last_name}`,
      });
    }

    modalOpen({
      title,
      subtitle,
      content: (
        <FormRemoveSession
          onCancel={modalClose}
          onSubmit={(values: any) => {
            removeSessionsInstanceRequest({
              values: {
                ...values,
                sessions_ids: sessions.map(({ session_id }) => session_id),
              },
            });
          }}
        />
      ),
      maxWidth: 'sm',
    });
  };

  const handleCheck = (uuid: string) => {
    const findCheckId = checks.find(item => item === uuid);
    if (!findCheckId) {
      setChecks([...checks, uuid]);
    } else {
      const index = checks.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const renderItem = (item: SessionsInInstance) => (
    <TableRow key={item?.session_id}>
      <Permission value="delete_environment_instance_sessions">
        <TableCell className={classes.checkboxCell}>
          <Checkbox
            onChange={() => handleCheck(item?.session_id)}
            checked={!!checks.find(check => check === item?.session_id)}
          />
        </TableCell>
      </Permission>
      <TableCell className={classes.text}>{item?.user.first_name}</TableCell>
      <TableCell className={classes.text}>{item?.user.email}</TableCell>
      <TableCell className={classes.text}>
        <Link
          onClick={modalClose}
          to={`/clients/${item?.client.client_uuid}/configurations`}
        >
          {item?.client.name}
        </Link>
      </TableCell>
      <TableCell centerTitle className={classes.text}>
        {formatDateWithMoment(item?.login_at)}
      </TableCell>
      <Tooltip
        title={i18n.t(
          item?.status === 'close'
            ? i18n.t('CLOSED_KEY')
            : `${item?.status.toUpperCase()}_KEY`
        )}
        placement="top"
        arrow
      >
        <TableCell centerTitle className={classes.text}>
          <Grid container justify="center">
            {mapStatus(item?.status)}
          </Grid>
        </TableCell>
      </Tooltip>
      <Permission value="delete_environment_instance_sessions">
        <TableCell textAlignCenter>
          <IconButton
            className={classes.buttonRemove}
            onClick={() => handleRemoveSessions([item], 'single')}
          >
            <Delete fontSize="small" />
          </IconButton>
        </TableCell>
      </Permission>
    </TableRow>
  );

  const getAllSessionsInInstance = React.useCallback(
    query => {
      getAllSessionsInInstanceRequest({
        environmentId,
        instanceId,
        query,
      });
    },
    [environmentId, instanceId, getAllSessionsInInstanceRequest]
  );

  React.useEffect(() => {
    getAllSessionsInInstance(query);
  }, [getAllSessionsInInstance, query]);

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      sessionsInInstanceList.results.forEach(item =>
        ids.push(item?.session_id)
      );
      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const headers = [
    {
      label: i18n.t('USER_KEY'),
    },
    {
      label: i18n.t('EMAIL_KEY'),
    },
    {
      label: i18n.t('CLIENT_NAME_KEY'),
    },
    {
      label: i18n.t('CREATE_DATE_KEY'),
      centerTitle: true,
    },
    {
      label: i18n.t('STATUS_KEY'),
      centerTitle: true,
    },
    {
      label: i18n.t('ACTIONS_KEY'),
      centerTitle: true,
      hidden: !permissions?.delete_environment_instance_sessions,
    },
  ].filter(item => !item.hidden);

  const [isSearchLocked, setIsSearchLocked] = React.useState(false);

  const initiateSearch = searchValue => {
    if (isSearchLocked || sessionsInInstanceList?.loading) return;
    setIsSearchLocked(true);

    handleSearch(searchValue);

    setTimeout(() => {
      setIsSearchLocked(false);
    }, 150);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>{i18n.t('SEARCH_KEY')}</InputLabel>
          <OutlinedInput
            type="text"
            labelWidth={70}
            onChange={({ target }) => setSearchValue(target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                initiateSearch(searchValue);
              }
            }}
          />
        </FormControl>
      </Grid>
      <Permission value="delete_environment_instance_sessions">
        <Grid className={classes.headerContent} container>
          <Button
            id="remove-session-bulk-client-button"
            variant="contained"
            color="inherit"
            className={classes.removeButton}
            startIcon={<Delete fontSize="small" />}
            onClick={() =>
              handleRemoveSessions(
                checks.map(session_id => ({ session_id })),
                'multiple'
              )
            }
            disabled={checks.length === 0}
          >
            {i18n.t('REMOVE_SESSIONS_KEY')}
          </Button>
        </Grid>
      </Permission>

      <Grid item xs={12}>
        <TableItems
          overrideRowsCount={10}
          header={
            <>
              <Permission value="delete_environment_instance_sessions">
                <TableCell className={classes.checkboxCell}>
                  <Checkbox onChange={handleAllCheck} />
                </TableCell>
              </Permission>
              {headers.map(header => (
                <TableCell
                  centerTitle={header?.centerTitle}
                  key={cyrb53Hash(header.label)}
                >
                  {header?.label}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: SessionsInInstance) => renderItem(item)}
          data={sessionsInInstanceList}
          isLoading={sessionsInInstanceList?.loading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('CLIENTS_PAGE.NO_SESSIONS_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttonsBase}>
        <Grid container className={classes.buttonsAlign}>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { sessionsInInstanceList },
}: ApplicationState) => ({
  permissions: data?.permissions,
  sessionsInInstanceList,
});

export default connect(mapStateToProps, {
  getAllSessionsInInstanceRequest,
  removeSessionsInstanceRequest,
  modalOpen,
  modalClose,
})(InstanceSessions);
